import { useCallback, useEffect } from 'react';
import {
  AddToCartParams,
  InitiateCheckoutParams,
  MetaPixelSettings,
  PurchaseParams,
  ViewContentParams,
} from '../types/metaPixel';
import {
  init,
  isPixelIdValid,
  trackAddToCart,
  trackInitiateCheckout,
  trackPageView,
  trackPurchase,
  trackViewContent,
} from '../utils/metaPixelHelpers';

interface UseMetaPixelProps {
  pixelId?: string | null;
  enabled?: boolean | null;
  settings?: MetaPixelSettings | null;
}

export function useMetaPixel({ pixelId, enabled, settings }: UseMetaPixelProps) {
  useEffect(() => {
    if (enabled && isPixelIdValid(pixelId)) {
      init(pixelId!);
      if (settings?.pageView) {
        trackPageView();
      }
    }
  }, [pixelId, enabled, settings?.pageView]);

  const handleTrackViewContent = useCallback((params: ViewContentParams) => {
    if (enabled && isPixelIdValid(pixelId) && settings?.viewContent) {
      trackViewContent(params);
    }
  }, [pixelId, enabled, settings?.viewContent]);

  const handleTrackAddToCart = useCallback((params: AddToCartParams) => {
    if (enabled && isPixelIdValid(pixelId) && settings?.addToCart) {
      trackAddToCart(params);
    }
  }, [pixelId, enabled, settings?.addToCart]);

  const handleTrackInitiateCheckout = useCallback((params: InitiateCheckoutParams) => {
    if (enabled && isPixelIdValid(pixelId) && settings?.initiateCheckout) {
      trackInitiateCheckout(params);
    }
  }, [pixelId, enabled, settings?.initiateCheckout]);

  const handleTrackPurchase = useCallback((params: PurchaseParams) => {
    if (enabled && isPixelIdValid(pixelId) && settings?.purchase) {
      trackPurchase(params);
    }
  }, [pixelId, enabled, settings?.purchase]);

  return {
    trackViewContent: handleTrackViewContent,
    trackAddToCart: handleTrackAddToCart,
    trackInitiateCheckout: handleTrackInitiateCheckout,
    trackPurchase: handleTrackPurchase,
  };
} 